import { sqWorkbookStore } from '@/core/core.stores';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import type {
  WorkbookApprovalFlow,
  WorkbookApprovalFlowAssignment,
  WorkbookApprovalFlowStep,
} from '@/worksheet/worksheet.types';

export type UseApprovalFlowsResult = {
  approvalFlow: WorkbookApprovalFlow | null;
  approvalFlowAssignments: WorkbookApprovalFlowAssignment[];
  approvedSteps: WorkbookApprovalFlowStep[] | undefined;
  pendingApprovalSteps: WorkbookApprovalFlowStep[] | undefined;
  nextStep: WorkbookApprovalFlowStep | undefined;
  futureStep: WorkbookApprovalFlowStep | undefined;
  hasAccessToAnyStep: boolean;
  showApplyBtn: boolean;
  showStepDropdown: boolean;
  workbookId: string;
  approved: boolean | undefined;
};

export const useApprovalFlows = (): UseApprovalFlowsResult => {
  const {
    approvalFlows,
    approvalFlowAssignments,
    approvalFlowAttachments,
    workbookId,
    effectivePermissions,
    isAnalysis,
    isArchived,
  } = useFluxPaths(sqWorkbookStore, [
    'approvalFlows',
    'approvalFlowAssignments',
    'approvalFlowAttachments',
    'workbookId',
    'effectivePermissions',
    'isAnalysis',
    'isArchived',
  ]);
  // Initial implementation assumes there would only 1 approvalFlows per server.
  const approvalFlow = approvalFlows[0] ?? null;
  const pendingApprovalSteps = approvalFlow?.steps.filter(({ status }) => status === 'PENDING');
  const approvedSteps = approvalFlow?.steps.filter(({ status }) => status === 'APPROVED');
  const nextStep = pendingApprovalSteps?.[0];
  const futureStep = pendingApprovalSteps?.[1];
  const hasAccessToAnyStep =
    approvalFlow?.steps.some((step) => !step.archived && Object.values(step.permissions).some((v) => v)) ?? false;
  const showApplyBtn =
    Boolean(approvalFlow) &&
    isAnalysis &&
    !isArchived &&
    effectivePermissions.manage &&
    !approvalFlowAttachments.length;
  const showStepDropdown = Boolean(approvalFlow) && isAnalysis && !isArchived && hasAccessToAnyStep;
  const approved = approvalFlow?.steps.every((step) => step.status === 'APPROVED');

  return {
    approvedSteps,
    pendingApprovalSteps,
    approvalFlow,
    approvalFlowAssignments,
    hasAccessToAnyStep,
    nextStep,
    futureStep,
    showApplyBtn,
    showStepDropdown,
    workbookId,
    approved,
  };
};
